import React, { useEffect } from 'react';
import { trpc } from '../trpc';

/**
 * Interface for UTM tracking data
 */
export interface UTMData {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  pagePath: string;
  referrer?: string;
  extraParams?: Record<string, string>;
  locale?: string;
  timezone?: string;
  userId?: string;
  capturedAt: string;
}

/**
 * Extracts UTM parameters from the current URL
 */
export function extractUTMParams(userId?: string): UTMData | null {
  if (typeof window === 'undefined') return null;
  
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  // Check if any UTM parameters exist
  const hasUTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    .some(param => params.has(param));
    
  if (!hasUTM) return null;
  
  return {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    term: params.get('utm_term') || undefined,
    content: params.get('utm_content') || undefined,
    pagePath: window.location.pathname,
    referrer: document.referrer || undefined,
    capturedAt: new Date().toISOString(),
    userId: userId,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
}

/**
 * Hook for tracking UTM parameters
 * ONLY runs in production mode on initial render only
 * Uses a ref, localStorage, and browser session ID to ensure uniqueness
 */
export function useUTMTracking() {
  const trackUtm = trpc.analytics.trackUtm.useMutation();
  const isInitialRender = React.useRef(true);
  
  useEffect(() => {
    // Skip in development mode
    if (import.meta.env.DEV) {
      console.log('UTM tracking disabled in development mode');
      return;
    }
    
    // Important: Only run on initial render to prevent multiple API calls
    if (!isInitialRender.current) {
      return;
    }
    
    // Mark that we've run this effect
    isInitialRender.current = false;
    
    // Generate or get a unique browser session ID
    // This helps distinguish between different browser sessions
    const storageSessionKey = 'trackplicant_browser_session_id';
    let sessionId = sessionStorage.getItem(storageSessionKey);
    if (!sessionId) {
      // Generate a random session ID if none exists
      sessionId = Math.random().toString(36).substring(2, 15) + 
                 Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem(storageSessionKey, sessionId);
    }
    
    // Create a unique ID for the current URL with UTM parameters
    const url = new URL(window.location.href);
    const urlPath = url.pathname;
    const searchParams = url.searchParams;
    
    // Extract all UTM parameters for more precise tracking
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');
    const utmTerm = searchParams.get('utm_term');
    const utmContent = searchParams.get('utm_content');
    
    // Only continue if we have at least one UTM parameter
    if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent) {
      return;
    }
    
    // Create a unique tracking ID that includes the session ID
    // This ensures we don't track the same UTM parameters twice in the same session
    const trackingId = `${sessionId}|${urlPath}|${utmSource || ''}|${utmMedium || ''}|${utmCampaign || ''}`;
    
    // Check if we've already tracked this exact combination in this session
    const trackedSessionUrlsKey = 'trackplicant_tracked_session_utm';
    const trackedSessionUrls = sessionStorage.getItem(trackedSessionUrlsKey);
    const trackedSessionUrlsArray = trackedSessionUrls ? JSON.parse(trackedSessionUrls) : [];
    
    // If this exact combination was already tracked in this session, skip
    if (trackedSessionUrlsArray.includes(trackingId)) {
      console.log('UTM parameters already tracked in this browser session');
      return;
    }
    
    // Check if we've already tracked this URL in localStorage (persists across sessions)
    // This is a secondary layer of protection against frequent duplicate visits
    const trackedUrlKey = 'trackplicant_tracked_utm_urls';
    const trackedUrls = localStorage.getItem(trackedUrlKey);
    
    // Define the type for tracking entries
    interface TrackingEntry {
      url: string;
      expires: number;
      timestamp: number;
    }
    
    const trackedUrlsArray: TrackingEntry[] = trackedUrls ? JSON.parse(trackedUrls) : [];
    
    // Generate a TTL (Time-To-Live) for this URL's tracking (6 hours)
    // Shortened from 24 hours to ensure we don't miss returning visitors
    const now = Date.now();
    const ttl = now + (6 * 60 * 60 * 1000); // 6 hours in milliseconds
    
    // Clean up expired tracking entries
    const validTrackingEntries = trackedUrlsArray.filter((entry: TrackingEntry) => entry.expires > now);
    
    // Check if there's an unexpired entry that matches this exact tracking ID
    const existingEntry = validTrackingEntries.find((entry: TrackingEntry) => entry.url === trackingId);
    if (existingEntry) {
      console.log('UTM parameters for this URL already tracked recently in this browser');
      return;
    }
    
    // Get UTM data for tracking
    const utmData = extractUTMParams();
    if (utmData) {
      console.log('Tracking UTM parameters:', utmData);
      
      // Track the UTM parameters - add a onSuccess handler to ensure we only
      // record in storage if the API call succeeds
      trackUtm.mutate(utmData, {
        onSuccess: () => {
          // Add this URL to the SESSION tracked URLs (prevents duplicates in same session)
          trackedSessionUrlsArray.push(trackingId);
          sessionStorage.setItem(trackedSessionUrlsKey, JSON.stringify(trackedSessionUrlsArray));
          
          // Also add to persistent localStorage with TTL
          validTrackingEntries.push({
            url: trackingId,
            expires: ttl,
            timestamp: now
          } as TrackingEntry);
          
          // Save back to localStorage
          localStorage.setItem(trackedUrlKey, JSON.stringify(validTrackingEntries));
          console.log('UTM parameters successfully tracked');
        },
        onError: (error: unknown) => {
          console.error('Error tracking UTM parameters:', error);
        }
      });
    }
  }, []); // Empty dependency array - critical to ensure this only runs once
}